import React from 'react';
import { useTranslation } from 'react-i18next';
import { SectionHead } from './SectionHead';
import { DocIcon } from './icons/DocIcon';
import { FileTextOutlined } from '@ant-design/icons';

// the PDF as a same-origin blob URL: a cross-origin file is neither downloaded by `download` nor printable in a frame
const blobUrlOf = async url => {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`PDF request failed: ${response.status}`);
    return URL.createObjectURL(new Blob([await response.blob()], { type: 'application/pdf' }));
};

const fileNameOf = url => decodeURIComponent(url.split('?')[0].split('/').pop() || 'report.pdf');

// saves the PDF; opens it in a new tab when it cannot be fetched
export const downloadPdf = async url => {
    try {
        const blobUrl = await blobUrlOf(url);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileNameOf(url);
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (error) {
        window.open(url, '_blank', 'noopener');
    }
};

// prints the PDF itself (not the page around it) from a hidden frame; opens it in a new tab when that is not possible
export const printPdf = async url => {
    try {
        const blobUrl = await blobUrlOf(url);
        const frame = document.createElement('iframe');
        frame.style.cssText = 'position:fixed;width:0;height:0;border:0;right:0;bottom:0';
        frame.onload = () => {
            frame.contentWindow.focus();
            frame.contentWindow.print();
            setTimeout(() => {
                frame.remove();
                URL.revokeObjectURL(blobUrl);
            }, 60000);
        };
        frame.src = blobUrl;
        document.body.appendChild(frame);
    } catch (error) {
        window.open(url, '_blank', 'noopener');
    }
};

// S8 / S9: the dealer's own PDF (watermarked) is the report, shown in the browser's PDF viewer
export const OriginalDocument = ({ original }) => {
    const { t } = useTranslation('reportPage');

    return (
        <section id="original" className="card" aria-labelledby="original-title" style={{ gap: 20 }}>
            <SectionHead id="original-title" icon={FileTextOutlined} title={t('original.title')} sub={original.file} />
            <div className={`notice nt-${original.tone}`} role="note">
                <DocIcon />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                    <span className="nt-t">{original.title}</span>
                    {/* "here is the document" holds on screen only: the PDF carries neither the viewer nor its
                        buttons, so it says where the document is instead (SC-QA-72) */}
                    {original.text && (
                        <span className={`nt-x${original.printNote ? ' noprint' : ''}`}>{original.text}</span>
                    )}
                    {original.printNote && <span className="nt-x print-only">{t('original.printNote')}</span>}
                </div>
            </div>
            {original.url && (
                <div className="pdfv noprint">
                    <object
                        className="pdf-embed"
                        data={original.url}
                        type="application/pdf"
                        aria-label={t('original.viewerAria')}
                    >
                        <p className="muted" style={{ margin: 16 }}>
                            {t('original.noViewer')}
                        </p>
                    </object>
                </div>
            )}
            <div className="actions noprint">
                {original.url && (
                    <>
                        <button type="button" className="btn btn-primary" onClick={() => downloadPdf(original.url)}>
                            {t('original.download')}
                        </button>
                        <button type="button" className="btn btn-secondary" onClick={() => printPdf(original.url)}>
                            {t('original.print')}
                        </button>
                    </>
                )}
                {original.optin && original.onOptin && (
                    <button type="button" className="textlink" onClick={original.onOptin}>
                        {original.optin}
                    </button>
                )}
            </div>
        </section>
    );
};
